import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Scale, Shield } from 'lucide-react';

/**
 * /impersonate?token=JWT
 *
 * Opened by the Platform Admin when impersonating a user.
 * Reads the token from URL, validates it via /api/auth/me,
 * stores it, then redirects to dashboard.
 *
 * Open in incognito to avoid overwriting your own session.
 */
export default function ImpersonatePage() {
  const [searchParams] = useSearchParams();
  const { login }      = useAuth();
  const navigate       = useNavigate();
  const [error,  setError]  = useState('');
  const [status, setStatus] = useState('Validating token…');

  useEffect(() => {
    const token = searchParams.get('token');
    if (!token) { setError('No impersonation token provided.'); return; }

    const BASE = process.env.REACT_APP_API_URL || '/api';

    fetch(`${BASE}/auth/me`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(user => {
        if (user.error) throw new Error(user.error);
        setStatus(`Logging in as ${user.first_name} ${user.last_name}…`);
        login(token, user);
        setTimeout(() => navigate('/'), 800);
      })
      .catch(err => {
        setError(err.message || 'Invalid or expired impersonation token.');
      });
  }, []);

  const style = {
    page: {
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'var(--zeus-black)', padding: 24,
    },
    card: {
      background: 'var(--zeus-surface)', border: '1px solid var(--zeus-border)',
      borderRadius: 'var(--radius-lg)', padding: '36px 32px',
      maxWidth: 400, width: '100%', textAlign: 'center',
    },
    icon: {
      width: 44, height: 44, borderRadius: 12,
      background: 'rgba(212,168,83,0.1)', border: '1px solid rgba(212,168,83,0.25)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      margin: '0 auto 16px', color: 'var(--zeus-gold)',
    },
  };

  return (
    <div style={style.page}>
      <div style={style.card}>
        <div style={style.icon}>
          <Shield size={22} />
        </div>
        <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1.2rem', marginBottom: 8 }}>
          Admin Impersonation
        </h2>
        {error ? (
          <>
            <p style={{ color: 'var(--status-red)', fontSize: '0.85rem', marginBottom: 16 }}>{error}</p>
            <button className="btn btn-ghost" onClick={() => navigate('/login')}>Go to Login</button>
          </>
        ) : (
          <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>
            {status}
          </p>
        )}
      </div>
    </div>
  );
}
